import { useState, useContext, useRef, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './CommissionPayout.css';

const API_URL = process.env.REACT_APP_API_URL;

const currentMonthDefault = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const partnerKey = p => `${p.partner_type}-${p.user_id}`;

const fmtPrice = n => n != null ? `₹${parseFloat(n).toFixed(2)}` : '—';
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

export default function CommissionPayout() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [month, setMonth] = useState(currentMonthDefault);
  const [preview, setPreview] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const selectAllRef = useRef(null);

  // Order drill-down state (shared across all partner rows)
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [orderDetailsLoading, setOrderDetailsLoading] = useState({});

  const allSelected = preview && preview.length > 0 && selectedKeys.size === preview.length;
  const someSelected = selectedKeys.size > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggleAll = () => {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(preview.map(partnerKey)));
  };

  const togglePartner = key => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleOrder = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!orderDetails[orderId]) {
      setOrderDetailsLoading(prev => ({ ...prev, [orderId]: true }));
      try {
        const { data } = await axios.get(`${API_URL}/sales-orders/${orderId}/commission`, auth());
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      } catch {
        setOrderDetails(prev => ({ ...prev, [orderId]: null }));
      } finally {
        setOrderDetailsLoading(prev => ({ ...prev, [orderId]: false }));
      }
    }
  };

  const selectedPartners = preview ? preview.filter(p => selectedKeys.has(partnerKey(p))) : [];
  const selectedTotal = selectedPartners.reduce((s, p) => Math.round((s + p.total_commission) * 100) / 100, 0);

  const handlePreview = async () => {
    setError('');
    setResult(null);
    setPreview(null);
    setSelectedKeys(new Set());
    setExpandedOrder(null);
    setOrderDetails({});
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API_URL}/commission-payout/preview`, {
        ...auth(),
        params: { month },
      });
      setPreview(res.data);
      setSelectedKeys(new Set(res.data.map(partnerKey)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRun = async () => {
    if (selectedKeys.size === 0) return;
    if (!window.confirm(`Credit commission to ${selectedKeys.size} partner(s) for ${month}? This cannot be undone.`)) return;
    setError('');
    setResult(null);
    setRunning(true);
    try {
      const selected = selectedPartners.map(p => ({ user_id: p.user_id, partner_type: p.partner_type }));
      const res = await axios.post(`${API_URL}/commission-payout/run`, { month, selected }, auth());
      setResult(res.data);
      setPreview(null);
      setSelectedKeys(new Set());
      setExpandedOrder(null);
      setOrderDetails({});
    } catch (err) {
      setError(err.response?.data?.error || 'Payout failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="cp-page">
      <h2 className="cp-title">Commission Payout</h2>

      <div className="cp-controls">
        <label className="cp-label">
          Month
          <input
            type="month"
            className="cp-month-input"
            value={month}
            onChange={e => {
              setMonth(e.target.value);
              setPreview(null);
              setResult(null);
              setSelectedKeys(new Set());
              setExpandedOrder(null);
              setOrderDetails({});
            }}
          />
        </label>
        <button className="cp-btn-preview" onClick={handlePreview} disabled={loadingPreview || !month}>
          {loadingPreview ? 'Loading…' : 'Preview'}
        </button>
        {preview && preview.length > 0 && (
          <button className="cp-btn-run" onClick={handleRun} disabled={running || selectedKeys.size === 0}>
            {running
              ? 'Crediting…'
              : `Credit to Wallets${selectedKeys.size > 0
                  ? ` (${selectedKeys.size} partner${selectedKeys.size !== 1 ? 's' : ''} · ₹${selectedTotal.toFixed(2)})`
                  : ''}`}
          </button>
        )}
      </div>

      {error && <div className="cp-error">{error}</div>}

      {/* Preview table */}
      {preview && (
        <div className="cp-section">
          {preview.length === 0 ? (
            <p className="cp-empty">No unpaid commissions found for {month}.</p>
          ) : (
            <>
              <p className="cp-summary">
                {preview.length} partner{preview.length !== 1 ? 's' : ''}
                {' · '}Total: <strong>₹{preview.reduce((s, p) => Math.round((s + p.total_commission) * 100) / 100, 0).toFixed(2)}</strong>
                {selectedKeys.size !== preview.length && (
                  <span className="cp-sel-note"> · {selectedKeys.size} selected · ₹{selectedTotal.toFixed(2)}</span>
                )}
              </p>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th className="cp-cb-col">
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Partner</th>
                    <th>Type</th>
                    <th>Orders</th>
                    <th className="cp-num">Commission</th>
                    <th className="cp-expand"></th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map(p => (
                    <PartnerRow
                      key={partnerKey(p)}
                      partner={p}
                      checked={selectedKeys.has(partnerKey(p))}
                      onToggle={() => togglePartner(partnerKey(p))}
                      expandedOrder={expandedOrder}
                      onToggleOrder={toggleOrder}
                      orderDetails={orderDetails}
                      orderDetailsLoading={orderDetailsLoading}
                    />
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Result after run */}
      {result && (
        <div className="cp-section">
          <div className="cp-result-header">
            Payout complete — credited <strong>₹{result.total_credited.toFixed(2)}</strong> to{' '}
            {result.credited.length} partner{result.credited.length !== 1 ? 's' : ''}.
            {result.errors.length > 0 && (
              <span className="cp-err-count"> {result.errors.length} error(s).</span>
            )}
          </div>

          {result.credited.length > 0 && (
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Type</th>
                  <th className="cp-num">Orders</th>
                  <th className="cp-num">Credited</th>
                </tr>
              </thead>
              <tbody>
                {result.credited.map(r => (
                  <tr key={r.ref_id}>
                    <td>{r.name}</td>
                    <td>
                      <span className={`cp-tag cp-tag-${r.partner_type}`}>
                        {r.partner_type === 'store' ? 'Store' : 'Promoter'}
                      </span>
                    </td>
                    <td className="cp-num">{r.order_count}</td>
                    <td className="cp-num cp-green">₹{r.total_commission.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {result.errors.length > 0 && (
            <div className="cp-errors-block">
              <p className="cp-errors-title">Errors</p>
              {result.errors.map((e, i) => (
                <div key={i} className="cp-error-row">
                  {e.name} ({e.order_count} order{e.order_count !== 1 ? 's' : ''}): {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PartnerRow({ partner, checked, onToggle, expandedOrder, onToggleOrder, orderDetails, orderDetailsLoading }) {
  const [expanded, setExpanded] = useState(false);

  const handleTogglePartner = () => setExpanded(e => !e);

  return (
    <>
      <tr className={`cp-partner-row${checked ? ' cp-row-selected' : ''}`}>
        <td className="cp-cb-col" onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={checked} onChange={onToggle} />
        </td>
        <td onClick={handleTogglePartner}>{partner.name}</td>
        <td onClick={handleTogglePartner}>
          <span className={`cp-tag cp-tag-${partner.partner_type}`}>
            {partner.partner_type === 'store' ? 'Store' : 'Promoter'}
          </span>
        </td>
        <td onClick={handleTogglePartner}>
          {partner.order_count}
          {partner.self_sale_commission > 0 && partner.referral_commission > 0 && (
            <span className="cp-breakdown">
              {' '}(S ₹{partner.self_sale_commission.toFixed(2)} + R ₹{partner.referral_commission.toFixed(2)})
            </span>
          )}
        </td>
        <td className="cp-num" onClick={handleTogglePartner}>₹{partner.total_commission.toFixed(2)}</td>
        <td className="cp-expand" onClick={handleTogglePartner}>{expanded ? '▲' : '▼'}</td>
      </tr>

      {expanded && partner.orders.map(o => {
        const isOpen = expandedOrder === o.order_id;
        const detail = orderDetails[o.order_id];
        const loading = orderDetailsLoading[o.order_id];
        return (
          <>
            <tr
              key={`${o.order_id}-${o.commission_category}`}
              className="cp-order-row cp-order-clickable"
              onClick={() => onToggleOrder(o.order_id)}
            >
              <td></td>
              <td className="cp-order-no">{o.order_no}</td>
              <td>
                <span className={`cp-cat cp-cat-${o.commission_category}`}>
                  {o.commission_category === 'self_sale' ? 'Self' : 'Referral'}
                </span>
              </td>
              <td className="cp-order-customer">{o.customer_name || '—'}</td>
              <td>{fmtDate(o.delivery_date)}</td>
              <td className="cp-num">₹{o.commission.toFixed(2)}</td>
              <td className="cp-expand cp-chevron-sm">{isOpen ? '▲' : '▼'}</td>
            </tr>

            {isOpen && (
              <tr key={`detail-${o.order_id}`} className="cp-lines-row">
                <td></td>
                <td colSpan={5}>
                  {loading && <p className="cp-lines-msg">Loading…</p>}
                  {!loading && !detail && <p className="cp-lines-msg cp-lines-err">Failed to load details.</p>}
                  {!loading && detail && (
                    <table className="cp-lines-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="cp-num">Qty</th>
                          <th className="cp-num">Price</th>
                          <th className="cp-num">Pre-GST</th>
                          <th className="cp-num">Comm%</th>
                          <th className="cp-num">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.lines.map(line => (
                          <tr key={line.line_id}>
                            <td>{line.product_name}</td>
                            <td className="cp-num">{line.qty}</td>
                            <td className="cp-num">{fmtPrice(line.unit_price)}</td>
                            <td className="cp-num">{fmtPrice(line.pre_gst_unit_price)}</td>
                            <td className="cp-num">{line.commission_percent}%</td>
                            <td className="cp-num">{fmtPrice(line.line_commission)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={5} className="cp-lines-total-label">Order total commission</td>
                          <td className="cp-num cp-lines-total">{fmtPrice(detail.total_commission)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </td>
              </tr>
            )}
          </>
        );
      })}
    </>
  );
}
